import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Paperclip, Send } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import { MAIL_EXPORT_LIMITS, type BesprechungInhalt, type MailComposeDraft, type MailTransportChoice, type SiteDocDetail } from '@symphenia/shared-types'
import { EigeneDateien } from '@/components/mail/EigeneDateien'
import { fehlertext } from '@/lib/fehlertext'
import { mailApi } from '@/lib/mail/mail.api'
import { anVerteilerSenden } from './api'

const eingabe = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
const feldTitel = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Betreff „Protokoll {Art} Nr. {n} — {Projekt}" (AP-49 7.1). */
export function verteilerBetreff(i: Pick<BesprechungInhalt, 'form' | 'art' | 'laufnummer'>, dok: Pick<SiteDocDetail, 'projectLabel' | 'title'>): string {
  const was = i.form === 'BERICHTIGUNG' ? dok.title : `Protokoll ${i.art} Nr. ${i.laufnummer}`
  return dok.projectLabel ? `${was} — ${dok.projectLabel}` : was
}

/**
 * AP-49 7.1 — **An den Verteiler senden.** Vorbelegt mit den Adressen aller Teilnehmer mit Haken
 * „Verteiler", dem Betreff und einem Text mit der Einspruchsfrist; das PDF hängt an. Der Benutzer kann
 * abwählen, Adressen ergänzen und den Text ändern. Mehr als 20 Empfänger gehen in mehreren Mails zu je 20
 * (BS-9) — das sagt der Dialog vorher.
 */
export function VerteilerDialog({ dok, onClose, onGesendet }: { dok: SiteDocDetail; onClose: () => void; onGesendet: () => void }) {
  const { t } = useTranslation()
  const i = dok.content as unknown as BesprechungInhalt
  const verteiler = useMemo(() => i.teilnehmer.filter((p) => p.distribution), [i.teilnehmer])
  const mitAdresse = verteiler.filter((p) => p.email && EMAIL.test(p.email))
  const ohneAdresse = verteiler.filter((p) => !p.email || !EMAIL.test(p.email))
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(() => new Set(mitAdresse.map((p) => p.email!.toLowerCase())))
  const [weitere, setWeitere] = useState('')
  const [entwurf, setEntwurf] = useState<MailComposeDraft | null>(null)
  const [weg, setWeg] = useState<MailTransportChoice | null>(null)
  const [kopie, setKopie] = useState(false)
  const [betreff, setBetreff] = useState(verteilerBetreff(i, dok))
  const [nachricht, setNachricht] = useState('')
  const [dateien, setDateien] = useState<File[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [gesendet, setGesendet] = useState<{ mails: number; recipients: number } | null>(null)

  useEffect(() => {
    mailApi
      .compose('SITE_DOCUMENT_DISTRIBUTION')
      .then(({ data }) => {
        setEntwurf(data.data)
        setWeg(data.data.defaultTransport)
        const frist = i.einspruch?.text ? `\n\n${i.einspruch.text}` : ''
        const text = i.form === 'BERICHTIGUNG' ? t('siteDocs.meeting.mailTextCorrection') : t('siteDocs.meeting.mailText', { art: i.art, nr: i.laufnummer })
        setNachricht(`${text}${frist}${data.data.signature ? `\n\n${data.data.signature}` : ''}`)
      })
      .catch((e) => setFehler(fehlertext(e, t)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const zusaetzlich = weitere
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean)
  const an = [...new Set([...mitAdresse.map((p) => p.email!).filter((a) => gewaehlt.has(a.toLowerCase())), ...zusaetzlich].map((a) => a.toLowerCase()))]
  const ungueltig = zusaetzlich.filter((a) => !EMAIL.test(a))
  const mails = Math.ceil(an.length / MAIL_EXPORT_LIMITS.recipients)
  const ueberMicrosoft = weg === 'MICROSOFT_365'

  const senden = async () => {
    setFehler(null)
    setLaeuft(true)
    try {
      const r = await anVerteilerSenden(dok.id, { to: an, subject: betreff.trim(), message: nachricht, copyToSelf: kopie && !ueberMicrosoft, ...(weg ? { transport: weg } : {}) }, dateien)
      setGesendet({ mails: r.mails.length, recipients: r.recipients })
      onGesendet()
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={t('siteDocs.meeting.distribute')}
      footer={
        <div className="flex justify-end gap-2">
          {gesendet ? (
            <button type="button" onClick={onClose} className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
              {t('common.close')}
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={laeuft || !entwurf || !weg || an.length === 0 || ungueltig.length > 0 || !betreff.trim()}
                onClick={() => void senden()}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {laeuft ? t('mailExport.sending') : t('siteDocs.meeting.distributeSubmit')}
              </button>
            </>
          )}
        </div>
      }
    >
      {gesendet ? (
        <div className="flex items-start gap-3 py-2" role="status">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
          <p className="text-sm">{t('siteDocs.meeting.distributed', { recipients: gesendet.recipients, mails: gesendet.mails })}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entwurf && !ueberMicrosoft && <p className="text-xs text-muted-foreground">{t('mailExport.replyTo', { email: entwurf.replyTo })}</p>}
          {entwurf && (entwurf.transports.microsoft365 || entwurf.transports.system) && (
            <div className="space-y-1">
              <span className={feldTitel}>{t('mailExport.from')}</span>
              {entwurf.transports.microsoft365 && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="verteilerWeg" disabled={entwurf.transports.microsoft365.status !== 'OK'} checked={weg === 'MICROSOFT_365'} onChange={() => setWeg('MICROSOFT_365')} />
                  {t('mailExport.fromMicrosoft', { address: entwurf.transports.microsoft365.address ?? '' })}
                </label>
              )}
              {entwurf.transports.system && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="verteilerWeg" checked={weg === 'SYSTEM'} onChange={() => setWeg('SYSTEM')} />
                  {t('mailExport.fromSystem', { address: entwurf.transports.system.address })}
                </label>
              )}
            </div>
          )}
          <div className="space-y-1">
            <span className={feldTitel}>{t('siteDocs.meeting.distributionList')}</span>
            <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded-md border p-2 text-sm">
              {mitAdresse.map((p) => {
                const k = p.email!.toLowerCase()
                return (
                  <li key={p.id ?? p.clientId}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={gewaehlt.has(k)}
                        onChange={(e) =>
                          setGewaehlt((g) => {
                            const n = new Set(g)
                            if (e.target.checked) n.add(k)
                            else n.delete(k)
                            return n
                          })
                        }
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {[p.name, p.company].filter(Boolean).join(', ')} <span className="text-muted-foreground">&lt;{p.email}&gt;</span>
                      </span>
                      {p.sentAt && <span className="text-xs text-muted-foreground">{t('siteDocs.meeting.sentOn', { am: new Date(p.sentAt).toLocaleDateString() })}</span>}
                    </label>
                  </li>
                )
              })}
              {mitAdresse.length === 0 && <li className="text-muted-foreground">{t('siteDocs.meeting.noAddresses')}</li>}
            </ul>
            {ohneAdresse.length > 0 && <p className="text-xs text-amber-800 dark:text-amber-200">{t('siteDocs.meeting.withoutAddress', { namen: ohneAdresse.map((p) => p.name).join(', ') })}</p>}
          </div>
          <label className="block space-y-1">
            <span className={feldTitel}>{t('siteDocs.meeting.moreRecipients')}</span>
            <input className={eingabe} value={weitere} onChange={(e) => setWeitere(e.target.value)} placeholder="name@example.com" aria-invalid={ungueltig.length > 0} />
          </label>
          {mails > 1 && <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.splitHint', { recipients: an.length, mails })}</p>}
          {!ueberMicrosoft && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={kopie} onChange={(e) => setKopie(e.target.checked)} />
              {t('mailExport.copyToSelf')}
            </label>
          )}
          <label className="block space-y-1">
            <span className={feldTitel}>{t('mailExport.subject')}</span>
            <input className={eingabe} value={betreff} maxLength={MAIL_EXPORT_LIMITS.subject} onChange={(e) => setBetreff(e.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className={feldTitel}>{t('mailExport.message')}</span>
            <textarea className={eingabe} rows={9} value={nachricht} maxLength={MAIL_EXPORT_LIMITS.message} onChange={(e) => setNachricht(e.target.value)} />
          </label>
          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="flex items-center gap-1.5 text-sm">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              {t('mailExport.attachment')}: {dok.number}.pdf
            </p>
            <div className="border-t border-border pt-2">
              <EigeneDateien dateien={dateien} onChange={setDateien} ueberMicrosoft={ueberMicrosoft} />
            </div>
          </div>
          {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        </div>
      )}
    </Modal>
  )
}
